/**
 * LYT Communications - Utility Locate Tracker
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Tracks 811 utility locate requests per segment.
 * Shows ticket numbers, call dates, expiration countdowns, status badges.
 * Admin/Supervisor can verify locates. Verified + unexpired locates are required before boring.
 */

import React, { useState, useCallback } from 'react';
import { Phone, CheckCircle, Clock, AlertTriangle, Plus, Shield, ChevronDown, ChevronUp, X } from 'lucide-react';

const LOCATE_STATUSES = ['Requested', 'Received', 'Verified', 'Expired'];
const UTILITY_TYPES = ['Electric', 'Gas', 'Water', 'Sewer', 'Telecom', 'Cable', 'Fiber', 'Other'];

const STATUS_BADGE_COLORS = {
  'Requested': '#FFB800',
  'Received': '#2196F3',
  'Verified': '#4CAF50',
  'Expired': '#FF4444',
};

function LocateTracker({ segment, locates = [], darkMode, user, onCreateLocate, onVerifyLocate, onUpdateLocate }) {
  const [expanded, setExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLocate, setNewLocate] = useState({
    ticket_number: '',
    call_date: new Date().toISOString().split('T')[0],
    expiration_date: '',
    utility_type: 'Electric',
    notes: '',
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'Supervisor' || user?.role === 'QC Inspector';
  const segmentLocates = locates.filter(loc => loc.segment_id === segment?.segment_id);

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const inputBg = darkMode ? '#0d1b2a' : '#ffffff';

  // Calculate days until expiration
  const getDaysUntilExpiry = useCallback((expirationDate) => {
    if (!expirationDate) return null;
    const now = new Date();
    const expiry = new Date(expirationDate);
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  }, []);

  // Check if locate is expired
  const isExpired = useCallback((loc) => {
    if (!loc.expiration_date) return false;
    return new Date(loc.expiration_date) < new Date();
  }, []);

  // Get effective status (auto-mark expired)
  const getEffectiveStatus = useCallback((loc) => {
    if (loc.status === 'Verified' && isExpired(loc)) return 'Expired';
    if (loc.status === 'Received' && isExpired(loc)) return 'Expired';
    return loc.status;
  }, [isExpired]);

  // Are all locates verified and not expired?
  const allVerified = segmentLocates.length > 0 && segmentLocates.every(loc => {
    const effectiveStatus = getEffectiveStatus(loc);
    return effectiveStatus === 'Verified';
  });

  const handleAddLocate = useCallback(async () => {
    if (!newLocate.ticket_number.trim()) return;
    await onCreateLocate?.({
      project_id: segment?.project_id || segment?.segment_id?.split('-').slice(0, 3).join('-') || '',
      segment_id: segment?.segment_id || '',
      ...newLocate,
    });
    setNewLocate({
      ticket_number: '',
      call_date: new Date().toISOString().split('T')[0],
      expiration_date: '',
      utility_type: 'Electric',
      notes: '',
    });
    setShowAddForm(false);
  }, [newLocate, segment, onCreateLocate]);

  const handleVerify = useCallback(async (locateId) => {
    await onVerifyLocate?.(locateId, user?.name || user?.email);
  }, [onVerifyLocate, user]);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${borderColor}`, background: inputBg, color: text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10,
      overflow: 'hidden', marginBottom: 8,
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${borderColor}` : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Phone size={16} color={accent} />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Utility Locates</span>
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
            background: allVerified ? '#4CAF5020' : segmentLocates.length === 0 ? '#FF444420' : '#FFB80020',
            color: allVerified ? '#4CAF50' : segmentLocates.length === 0 ? '#FF4444' : '#FFB800',
          }}>
            {allVerified ? 'All Verified' : segmentLocates.length === 0 ? 'No Locates' : 'Pending'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: textMuted }}>{segmentLocates.length} locate{segmentLocates.length !== 1 ? 's' : ''}</span>
          {expanded ? <ChevronUp size={14} color={textMuted} /> : <ChevronDown size={14} color={textMuted} />}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px' }}>
          {/* Existing locates */}
          {segmentLocates.length === 0 && (
            <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '12px 0' }}>
              No utility locates recorded. Add one before boring can begin.
            </div>
          )}

          {segmentLocates.map((loc, idx) => {
            const effectiveStatus = getEffectiveStatus(loc);
            const daysLeft = getDaysUntilExpiry(loc.expiration_date);
            const badgeColor = STATUS_BADGE_COLORS[effectiveStatus] || '#9E9E9E';
            const isWarning = daysLeft !== null && daysLeft <= 7 && daysLeft > 0;

            return (
              <div key={loc.locate_id || idx} style={{
                padding: '10px 12px', borderRadius: 8,
                border: `1px solid ${borderColor}`, marginBottom: 6,
                background: darkMode ? '#0d1b2a' : '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: text }}>#{loc.ticket_number}</span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 8, fontWeight: 600,
                      background: `${badgeColor}20`, color: badgeColor,
                    }}>
                      {effectiveStatus}
                    </span>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 8,
                      background: darkMode ? '#1e3a5f' : '#e2e8f0', color: textMuted,
                    }}>
                      {loc.utility_type}
                    </span>
                  </div>

                  {/* Verify button (admin only, for Received status) */}
                  {isAdmin && effectiveStatus === 'Received' && (
                    <button
                      onClick={() => handleVerify(loc.locate_id)}
                      style={{
                        padding: '4px 10px', fontSize: 11, fontWeight: 600,
                        background: '#4CAF50', color: '#fff', border: 'none',
                        borderRadius: 6, cursor: 'pointer', display: 'flex',
                        alignItems: 'center', gap: 4,
                      }}
                    >
                      <Shield size={12} /> Verify
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 16, fontSize: 11, color: textMuted }}>
                  <span>Called: {loc.call_date || '—'}</span>
                  <span>Expires: {loc.expiration_date || '—'}</span>
                  {daysLeft !== null && (
                    <span style={{
                      fontWeight: 600,
                      color: daysLeft <= 0 ? '#FF4444' : isWarning ? '#FFB800' : '#4CAF50',
                    }}>
                      {daysLeft <= 0 ? 'EXPIRED' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
                    </span>
                  )}
                </div>

                {loc.verified_by && (
                  <div style={{ fontSize: 10, color: textMuted, marginTop: 4 }}>
                    <CheckCircle size={10} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Verified by {loc.verified_by} on {loc.verified_date ? new Date(loc.verified_date).toLocaleDateString() : '—'}
                  </div>
                )}

                {loc.notes && (
                  <div style={{ fontSize: 11, color: textMuted, marginTop: 4, fontStyle: 'italic' }}>{loc.notes}</div>
                )}

                {/* Warning banner for expiring soon */}
                {isWarning && effectiveStatus !== 'Expired' && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                    padding: '4px 8px', borderRadius: 6, background: '#FFB80015',
                    fontSize: 11, color: '#FFB800', fontWeight: 600,
                  }}>
                    <AlertTriangle size={12} /> Expiring soon — renew before boring
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Locate Form */}
          {showAddForm ? (
            <div style={{
              padding: 12, borderRadius: 8, border: `1px solid ${accent}40`,
              background: darkMode ? '#0d1b2a' : '#f0f9ff', marginTop: 8,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: text }}>New Locate Request</span>
                <button onClick={() => setShowAddForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                  <X size={14} color={textMuted} />
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Ticket Number *</label>
                  <input
                    value={newLocate.ticket_number}
                    onChange={e => setNewLocate({ ...newLocate, ticket_number: e.target.value })}
                    placeholder="811 ticket #"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Utility Type</label>
                  <select
                    value={newLocate.utility_type}
                    onChange={e => setNewLocate({ ...newLocate, utility_type: e.target.value })}
                    style={inputStyle}
                  >
                    {UTILITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Call Date</label>
                  <input
                    type="date"
                    value={newLocate.call_date}
                    onChange={e => setNewLocate({ ...newLocate, call_date: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Expiration Date</label>
                  <input
                    type="date"
                    value={newLocate.expiration_date}
                    onChange={e => setNewLocate({ ...newLocate, expiration_date: e.target.value })}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Notes</label>
                <input
                  value={newLocate.notes}
                  onChange={e => setNewLocate({ ...newLocate, notes: e.target.value })}
                  placeholder="Any details about the locate..."
                  style={inputStyle}
                />
              </div>

              <button
                onClick={handleAddLocate}
                disabled={!newLocate.ticket_number.trim()}
                style={{
                  width: '100%', padding: '8px 0', fontSize: '0.8125rem', fontWeight: 600,
                  background: newLocate.ticket_number.trim() ? accent : '#9E9E9E',
                  color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer',
                }}
              >
                Save Locate
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                width: '100%', padding: '8px 0', fontSize: 12, fontWeight: 600,
                background: 'transparent', color: accent,
                border: `1px dashed ${accent}40`, borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                marginTop: 6,
              }}
            >
              <Plus size={14} /> Add Locate Request
            </button>
          )}

          {/* Summary bar */}
          {segmentLocates.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 10px', marginTop: 8, borderRadius: 6,
              background: allVerified ? '#4CAF5010' : '#FFB80010',
              border: `1px solid ${allVerified ? '#4CAF5030' : '#FFB80030'}`,
              fontSize: 11, fontWeight: 600,
            }}>
              <span style={{ color: allVerified ? '#4CAF50' : '#FFB800' }}>
                {allVerified ? (
                  <><CheckCircle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />All locates verified — clear to proceed</>
                ) : (
                  <><Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />Locates must be verified before boring</>
                )}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LocateTracker;
